import { v2Api } from "./client"
import type {
  V2ActivitiesPage,
  V2Activity,
  V2ActivityRegistrationsPage,
  V2Case,
  V2CasesPage,
  V2ConversationMessagesPage,
  V2ConversationMessage,
  V2ConversationSummary,
  V2EventConversationsResponse,
  V2EventPeoplePage,
  V2EventPerson,
} from "./contracts"

export async function listAllEventPeople(eventId: string): Promise<V2EventPerson[]> {
  const people: V2EventPerson[] = []
  let cursor: V2EventPeoplePage["page"]["nextCursor"] = null
  do {
    const page: V2EventPeoplePage = await v2Api<V2EventPeoplePage>(
      `/api/v2/events/${encodeURIComponent(eventId)}/people`,
      {
        searchParams: {
          limit: 100,
          cursorDisplayName: cursor?.displayName,
          cursorEventPersonId: cursor?.eventPersonId,
        },
      },
    )
    people.push(...page.people)
    cursor = page.page.hasMore ? page.page.nextCursor : null
  } while (cursor)
  return people
}

export async function getEventPerson(
  eventId: string,
  eventPersonId: string,
): Promise<V2EventPerson> {
  const response = await v2Api<{ success: true; person: V2EventPerson }>(
    `/api/v2/events/${encodeURIComponent(eventId)}/people/${encodeURIComponent(eventPersonId)}`,
  )
  return response.person
}

export async function getMyV2Profile() {
  const response = await v2Api<{
    success: true
    person: {
      id: string
      displayName: string
      emailDisplay: string
      avatarImageId: string | null
    }
  }>("/api/v2/me/profile")
  return response.person
}

export async function listAllActivities(eventId: string): Promise<V2Activity[]> {
  const activities: V2Activity[] = []
  let cursor: V2ActivitiesPage["page"]["nextCursor"] = null
  do {
    const page: V2ActivitiesPage = await v2Api<V2ActivitiesPage>(
      `/api/v2/events/${encodeURIComponent(eventId)}/activities`,
      {
        searchParams: {
          limit: 100,
          cursorStartsAt: cursor?.startsAt,
          cursorActivityId: cursor?.activityId,
        },
      },
    )
    activities.push(...page.activities)
    cursor = page.page.hasMore ? page.page.nextCursor : null
  } while (cursor)
  return activities
}

export async function listAllEventCases(eventId: string): Promise<V2Case[]> {
  const cases: V2Case[] = []
  let before: V2CasesPage["page"]["nextCursor"] = null
  do {
    const page: V2CasesPage = await v2Api<V2CasesPage>(
      `/api/v2/events/${encodeURIComponent(eventId)}/cases`,
      { searchParams: { limit: 200, before } },
    )
    cases.push(...page.cases)
    before = page.page.hasMore ? page.page.nextCursor : null
  } while (before)
  return cases
}

export async function getActivity(
  eventId: string,
  activityId: string,
): Promise<V2Activity> {
  const response = await v2Api<{ success: true; activity: V2Activity }>(
    `/api/v2/events/${encodeURIComponent(eventId)}/activities/${encodeURIComponent(activityId)}`,
  )
  return response.activity
}

export async function listAllActivityRegistrations(
  eventId: string,
  activityId: string,
): Promise<V2ActivityRegistrationsPage> {
  let cursor: V2ActivityRegistrationsPage["page"]["nextCursor"] = null
  let result: V2ActivityRegistrationsPage | null = null
  do {
    const page: V2ActivityRegistrationsPage = await v2Api<V2ActivityRegistrationsPage>(
      `/api/v2/events/${encodeURIComponent(eventId)}/activities/${encodeURIComponent(activityId)}/registrations`,
      {
        searchParams: {
          limit: 100,
          cursorCreatedAt: cursor?.createdAt,
          cursorRegistrationId: cursor?.registrationId,
        },
      },
    )
    result = result
      ? { ...page, registrations: [...result.registrations, ...page.registrations] }
      : page
    cursor = page.page.hasMore ? page.page.nextCursor : null
  } while (cursor)

  if (!result) throw new Error("The activity registration response was empty")
  return result
}

export async function issueEventInvitation(input: {
  eventId: string
  email: string
  firstName: string
  lastName?: string
  displayName?: string
  title?: string
  company?: string
  ticketTypeId?: string
  attendanceMode?: "offline" | "online" | "mixed"
}) {
  return v2Api<{
    success: true
    invitation: { id: string; eventPersonId: string; registrationId: string; status: string }
    registration: { id: string; status: string; scopeKind: "event" }
  }>(`/api/v2/events/${encodeURIComponent(input.eventId)}/invitations`, {
    method: "POST",
    body: {
      idempotencyKey: crypto.randomUUID(),
      email: input.email,
      firstName: input.firstName,
      ...(input.lastName ? { lastName: input.lastName } : {}),
      ...(input.displayName ? { displayName: input.displayName } : {}),
      ...(input.title ? { title: input.title } : {}),
      ...(input.company ? { company: input.company } : {}),
      ...(input.ticketTypeId ? { ticketTypeId: input.ticketTypeId } : {}),
      attendanceMode: input.attendanceMode ?? "offline",
      roleKey: "attendee",
    },
  })
}

export async function createV2Activity(input: {
  eventId: string
  name: string
  slug: string
  description?: string | null
  venueName?: string | null
  startsAt: string
  endsAt: string
  ianaTimezone: string
  maxCapacity?: number | null
}) {
  return v2Api<{ success: true; activity: V2Activity }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/activities`,
    {
      method: "POST",
      body: {
        idempotencyKey: crypto.randomUUID(),
        name: input.name,
        slug: input.slug,
        startsAt: input.startsAt,
        endsAt: input.endsAt,
        ianaTimezone: input.ianaTimezone,
        ...(input.description != null ? { description: input.description } : {}),
        ...(input.venueName != null ? { venueName: input.venueName } : {}),
        ...(input.maxCapacity != null ? { maxCapacity: input.maxCapacity } : {}),
      },
    },
  )
}

export function publishV2Activity(eventId: string, activityId: string) {
  return v2Api<{ success: true; activity: V2Activity }>(
    `/api/v2/events/${encodeURIComponent(eventId)}/activities/${encodeURIComponent(activityId)}/publish`,
    {
      method: "POST",
      body: { idempotencyKey: crypto.randomUUID() },
    },
  )
}

export function registerEventPersonForActivity(input: {
  eventId: string
  activityId: string
  eventPersonId: string
  attendanceMode?: "offline" | "online" | "mixed"
}) {
  return v2Api(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/activities/${encodeURIComponent(input.activityId)}/registrations`,
    {
      method: "POST",
      body: {
        idempotencyKey: crypto.randomUUID(),
        eventPersonId: input.eventPersonId,
        attendanceMode: input.attendanceMode ?? "offline",
      },
    },
  )
}

export function cancelActivityRegistration(input: {
  eventId: string
  activityId: string
  registrationId: string
  reason: string
}) {
  return v2Api(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/activities/${encodeURIComponent(input.activityId)}/registrations/${encodeURIComponent(input.registrationId)}/cancel`,
    {
      method: "POST",
      body: { idempotencyKey: crypto.randomUUID(), reason: input.reason },
    },
  )
}

export function promoteNextActivityWaitlistRegistration(
  eventId: string,
  activityId: string,
) {
  return v2Api(
    `/api/v2/events/${encodeURIComponent(eventId)}/activities/${encodeURIComponent(activityId)}/registrations/promote-next`,
    {
      method: "POST",
      body: { idempotencyKey: crypto.randomUUID() },
    },
  )
}

export function listEventConversations(eventId: string) {
  return v2Api<V2EventConversationsResponse>(
    `/api/v2/events/${encodeURIComponent(eventId)}/conversations`,
    { searchParams: { limit: 200 } },
  )
}

export function listEventConversationMessages(input: {
  eventId: string
  conversationId: string
  limit?: number
  beforeSequence?: number | null
}) {
  return v2Api<V2ConversationMessagesPage>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations/${encodeURIComponent(input.conversationId)}/messages`,
    {
      searchParams: {
        limit: input.limit ?? 100,
        beforeSequence: input.beforeSequence,
      },
    },
  )
}

export function createEventConversation(input: {
  eventId: string
  kind: "channel" | "direct" | "announcement"
  title: string
  topic?: string
  originActivityId?: string
  personIds?: string[]
  staffPersonIds?: string[]
  staffAudience?: "all" | "selected"
  segmentIds?: string[]
  choiceGroupIds?: string[]
  cohort?: { kind: string; key: string } | null
  includeAllConfirmedAttendees?: boolean
  postingMode?: "members" | "organizers-only"
  emoji?: string | null
  pinned?: boolean
}) {
  return v2Api<{ success: true; conversation: V2ConversationSummary }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations`,
    {
      method: "POST",
      body: {
        idempotencyKey: crypto.randomUUID(),
        kind: input.kind,
        title: input.title,
        ...(input.topic ? { topic: input.topic } : {}),
        ...(input.originActivityId ? { originActivityId: input.originActivityId } : {}),
        personIds: input.personIds ?? [],
        staffPersonIds: input.staffPersonIds ?? [],
        staffAudience: input.staffAudience ?? "all",
        segmentIds: input.segmentIds ?? [],
        choiceGroupIds: input.choiceGroupIds ?? [],
        ...(input.cohort ? { cohort: input.cohort } : {}),
        includeAllConfirmedAttendees: input.includeAllConfirmedAttendees ?? false,
        postingMode: input.postingMode ?? "members",
        ...(input.emoji != null ? { emoji: input.emoji } : {}),
        pinned: input.pinned ?? false,
      },
    },
  )
}

export function postEventConversationMessage(input: {
  eventId: string
  conversationId: string
  body: string
  bodyFormat?: "plain_text" | "markdown"
  replyToMessageId?: string
  richTextDoc?: unknown
  attachmentManifest?: Array<Record<string, unknown>>
  urgency?: "normal" | "urgent"
  clientId?: string
  reflectedFromThreadRootId?: string
}) {
  return v2Api<{ success: true; message: V2ConversationMessage }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations/${encodeURIComponent(input.conversationId)}/messages`,
    {
      method: "POST",
      body: {
        idempotencyKey: crypto.randomUUID(),
        body: input.body,
        bodyFormat: input.bodyFormat ?? "plain_text",
        ...(input.replyToMessageId ? { replyToMessageId: input.replyToMessageId } : {}),
        ...(input.richTextDoc ? { richTextDoc: input.richTextDoc } : {}),
        ...(input.attachmentManifest?.length
          ? { attachmentManifest: input.attachmentManifest }
          : {}),
        ...(input.urgency ? { urgency: input.urgency } : {}),
        ...(input.clientId ? { clientId: input.clientId } : {}),
        ...(input.reflectedFromThreadRootId
          ? { reflectedFromThreadRootId: input.reflectedFromThreadRootId }
          : {}),
      },
    },
  )
}

export function listEventConversationMembers(eventId: string, conversationId: string) {
  return v2Api<{
    success: true
    members: Array<{
      id: string
      conversationId: string
      personId: string
      role: string | null
      displayName?: string | null
      email?: string | null
    }>
  }>(
    `/api/v2/events/${encodeURIComponent(eventId)}/conversations/${encodeURIComponent(conversationId)}/members`,
  )
}

export function setEventConversationMembers(input: {
  eventId: string
  conversationId: string
  personIds: string[]
}) {
  return v2Api<{
    success: true
    members: Array<{ personId: string; role: string | null }>
  }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations/${encodeURIComponent(input.conversationId)}/members`,
    {
      method: "PUT",
      body: { idempotencyKey: crypto.randomUUID(), personIds: input.personIds },
    },
  )
}

export function patchEventConversation(input: {
  eventId: string
  conversationId: string
  patch: Record<string, unknown>
}) {
  return v2Api<{ success: true; conversation: V2ConversationSummary }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations/${encodeURIComponent(input.conversationId)}`,
    { method: "PATCH", body: input.patch },
  )
}

export function archiveEventConversation(input: {
  eventId: string
  conversationId: string
  archived: boolean
}) {
  return v2Api<{ success: true; conversation: V2ConversationSummary }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations/${encodeURIComponent(input.conversationId)}/archive`,
    {
      method: "POST",
      body: { idempotencyKey: crypto.randomUUID(), archived: input.archived },
    },
  )
}

export function markEventConversationRead(eventId: string, conversationId: string) {
  return v2Api<{ success: true; lastReadSequence: number }>(
    `/api/v2/events/${encodeURIComponent(eventId)}/conversations/${encodeURIComponent(conversationId)}/read`,
    { method: "POST", body: { idempotencyKey: crypto.randomUUID() } },
  )
}

export function editEventConversationMessage(input: {
  eventId: string
  conversationId: string
  messageId: string
  body: string
  richTextDoc?: unknown
  attachmentManifest?: Array<Record<string, unknown>>
}) {
  return v2Api<{ success: true; message: V2ConversationMessage }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations/${encodeURIComponent(input.conversationId)}/messages/${encodeURIComponent(input.messageId)}`,
    {
      method: "PATCH",
      body: {
        body: input.body,
        ...(input.richTextDoc ? { richTextDoc: input.richTextDoc } : {}),
        attachmentManifest: input.attachmentManifest ?? [],
      },
    },
  )
}

export function deleteEventConversationMessage(input: {
  eventId: string
  conversationId: string
  messageId: string
}) {
  return v2Api<{ success: true; messageId: string }>(
    `/api/v2/events/${encodeURIComponent(input.eventId)}/conversations/${encodeURIComponent(input.conversationId)}/messages/${encodeURIComponent(input.messageId)}`,
    { method: "DELETE" },
  )
}

export function listEventMessageReactions(input: {
  eventId: string
  parentType: "chat" | "ticket"
  parentIds: string[]
}) {
  return v2Api<{
    success: true
    data: Record<string, Array<{
      emoji: string
      count: number
      mine: boolean
      reactorPersonIds: string[]
    }>>
  }>(`/api/v2/events/${encodeURIComponent(input.eventId)}/message-reactions`, {
    searchParams: { parentType: input.parentType, parentIds: input.parentIds.join(",") },
  })
}

export function toggleEventMessageReaction(input: {
  eventId: string
  parentType: "chat" | "ticket"
  parentId: string
  emoji: string
}) {
  return v2Api<{
    success: true
    committed: boolean
    op: "add" | "remove"
    emoji: string
    count: number
  }>(`/api/v2/events/${encodeURIComponent(input.eventId)}/message-reactions/toggle`, {
    method: "POST",
    body: { idempotencyKey: crypto.randomUUID(), ...input },
  })
}
